import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Field, NSelect } from "@/components/common";
import { ServiceOptions } from "@/pages/QuoteForm";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const EMPTY = { title: "", client_name: "", site: "", service_id: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", team: [], hours: 0, progress: 0, status: "planifiee", description: "", tasks_done: "", materials: "", issues: "", recommendations: "" };
const TEXTS = [["description", "Contexte et objectifs"], ["tasks_done", "Travaux réalisés"], ["materials", "Matériel et pièces utilisés"], ["issues", "Difficultés rencontrées"], ["recommendations", "Recommandations"]];

export default function InterventionForm() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [services, setServices] = useState([]);
  const [names, setNames] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/team-names").then((r) => setNames(r.data));
    if (id) api.get(`/interventions/${id}`).then((r) => setF({ ...EMPTY, ...r.data, service_id: r.data.service_id || "", end_date: r.data.end_date || "" }));
  }, [id]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (n) => set("team", f.team.includes(n) ? f.team.filter((x) => x !== n) : [...f.team, n]);

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, service_id: f.service_id || null, end_date: f.end_date || null, hours: Number(f.hours), progress: Number(f.progress) };
    ["id", "number", "report", "archived", "archived_at", "created_at", "created_by"].forEach((k) => delete body[k]);
    try {
      const r = id ? await api.put(`/interventions/${id}`, body) : await api.post("/interventions", body);
      if (r.data.archived) {
        toast.success("Intervention clôturée : rapport généré et archivé");
        navigate(user.role === "admin" ? `/interventions/${r.data.id}` : "/interventions");
      } else {
        toast.success("Intervention enregistrée, rapport mis à jour");
        navigate(`/interventions/${r.data.id}`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <form onSubmit={save} data-testid="intervention-form">
      <PageHeader kicker="Suivi opérationnel" title={id ? `Modifier ${f.number || ""}` : "Nouvelle intervention"}>
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
        <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="intervention-save-btn"><Save className="mr-1 h-4 w-4" /> Enregistrer</Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <Panel title="Projet / intervention">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Intitulé *" className="sm:col-span-2"><Input required value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="iv-title-input" /></Field>
              <Field label="Client *"><Input required value={f.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="iv-client-input" /></Field>
              <Field label="Site *"><Input required value={f.site} onChange={(e) => set("site", e.target.value)} data-testid="iv-site-input" /></Field>
              <Field label="Pôle / service" className="sm:col-span-2">
                <NSelect value={f.service_id} onChange={(v) => set("service_id", v)} testId="iv-service-select">
                  <option value="">— Choisir —</option>
                  <ServiceOptions services={services} />
                </NSelect>
              </Field>
              <Field label="Début *"><Input type="date" required value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="iv-start-input" /></Field>
              <Field label="Fin"><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="iv-end-input" /></Field>
            </div>
          </Panel>
          <Panel title="Données techniques du rapport">
            <div className="space-y-4">
              {TEXTS.map(([k, l]) => (
                <Field key={k} label={l}><Textarea rows={3} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`iv-${k}-input`} /></Field>
              ))}
            </div>
          </Panel>
        </div>
        <aside className="h-fit space-y-4 rounded-md border border-slate-200 bg-white p-5 lg:sticky lg:top-24">
          <Field label="Statut">
            <NSelect value={f.status} onChange={(v) => set("status", v)} testId="iv-status-select">
              <option value="planifiee">Planifiée</option>
              <option value="en_cours">En cours</option>
              <option value="terminee">Terminée (archivage auto)</option>
            </NSelect>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Avancement %"><Input type="number" min="0" max="100" value={f.progress} onChange={(e) => set("progress", e.target.value)} data-testid="iv-progress-input" /></Field>
            <Field label="Heures"><Input type="number" min="0" step="any" value={f.hours} onChange={(e) => set("hours", e.target.value)} data-testid="iv-hours-input" /></Field>
          </div>
          <Field label="Équipe intervenante">
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="iv-team-list">
              {names.length ? names.map((n) => (
                <label key={n} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={f.team.includes(n)} onChange={() => toggle(n)} data-testid={`iv-team-${n.replace(/\s+/g, "-")}`} className="accent-red-600" />
                  {n}
                </label>
              )) : <p className="text-xs text-slate-500">Aucun employé enregistré.</p>}
            </div>
          </Field>
        </aside>
      </div>
    </form>
  );
}
